/**
 * The single place that decides what a platform card's connection really is.
 *
 * The grid row is assembled in `MCP.tsx` from the catalog entry plus the raw
 * `tara_credentials` row returned by `GET /tara/mcp/{shop_slug}/credentials`.
 * Three independent flags describe the connection and, until this helper
 * existed, the card rendered its pill from `is_active` alone — so a credential
 * the backend had already rejected still read "Connected" in green with only a
 * small warning triangle next to it.
 *
 * What the flags mean (verified against central-api):
 *
 * - `is_active`   — the user-facing on/off switch of the connection.
 * - `is_valid`    — OVERLOADED. `false` means either "the credential was
 *                   rejected, expired or revoked" OR "OAuth finished but the
 *                   account has not been picked yet". The controller writes
 *                   `is_active = false` together with `is_valid = false` for
 *                   that second, pending case, which is how the two are told
 *                   apart here: a pending setup is never labelled dead.
 * - `oauth_required` — computed by the controller: the platform uses OAuth and
 *                   the grant is missing or its refresh token has expired. It
 *                   can be true while `is_valid` is still true, i.e. the
 *                   credential works today but needs re-consent.
 *
 * `is_valid` is a NOT NULL column, so a row that exists always carries it.
 * It is still treated as "unknown" when absent instead of being defaulted to
 * true: failing open is exactly the bug this helper replaces.
 *
 * Built-in (`is_default`) rows are the always-on servers ADUP operates. They
 * carry no user credential, so they only ever read connected / deactivated /
 * not connected — this helper never starts warning on them.
 */

export type McpConnectionStatus =
  | 'not_connected'
  | 'deactivated'
  | 'setup_incomplete'
  | 'reconnect_needed'
  | 'action_needed'
  | 'unknown'
  | 'connected';

/**
 * The pill palette `mcpStatusCard.scss` styles via `data-mcp-status`.
 * `yellow` is the amber warning tone (#f8a61b), `blue` the informational one.
 */
export type McpPillColor = 'green' | 'yellow' | 'red' | 'blue';

/** The subset of a grid row this decision needs. */
export interface McpConnectionRow {
  connection_id?: number | string | null;
  is_active?: boolean | null;
  is_valid?: boolean | null;
  is_default?: boolean | null;
  oauth_required?: boolean | null;
  oauth_url?: string | null;
  /**
   * The account pickers the OAuth callback stored, keyed by credential field
   * (`{ AD_ACCOUNT_ID: [{ OptionValue, OptionName }] }`). `MANAGER_MAP` and
   * other non-list entries are ignored.
   */
  accounts?: Record<string, unknown> | null;
  /** The saved credential values, keyed by credential field. */
  credentials?: Record<string, unknown> | null;
}

export interface McpConnectionStatusView {
  status: McpConnectionStatus;
  /** i18n key for the pill label — resolve with `t()` at the call site. */
  labelKey: string;
  color: McpPillColor;
}

const STATUS_VIEW: Record<
  McpConnectionStatus,
  Omit<McpConnectionStatusView, 'status'>
> = {
  not_connected: { labelKey: 'NotConnected', color: 'red' },
  deactivated: { labelKey: 'Deactivated', color: 'yellow' },
  setup_incomplete: { labelKey: 'MCP-FinishSetup', color: 'blue' },
  reconnect_needed: { labelKey: 'MCP-ReconnectNeeded', color: 'red' },
  action_needed: { labelKey: 'MCP-ActionNeeded', color: 'yellow' },
  unknown: { labelKey: 'MCP-StatusUnknown', color: 'yellow' },
  connected: { labelKey: 'Connected', color: 'green' },
};

const hasValue = (value: unknown) => value !== undefined && value !== null;

const hasUsableOauthUrl = (row: McpConnectionRow) => {
  const url = row.oauth_url;
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed !== '' && trimmed !== 'null';
};

/**
 * True while the OAuth callback has stored an account picker but no account
 * from it has been saved yet. That is the only shape of an unfinished setup:
 * the callback parks the row inactive and invalid, fills `accounts`, and the
 * account field stays empty until the user picks one. A row without a picker
 * cannot be told apart from a switched-off one, so it is not treated as
 * unfinished.
 */
export const isAccountChoicePending = (
  row: Pick<McpConnectionRow, 'accounts' | 'credentials'>
): boolean => {
  const accounts = row.accounts;
  if (!accounts || typeof accounts !== 'object') return false;
  const pickerFields = Object.keys(accounts).filter((field) =>
    Array.isArray(accounts[field])
  );
  if (pickerFields.length === 0) return false;
  return pickerFields.every((field) => {
    const value = row.credentials?.[field];
    return value === undefined || value === null || String(value).trim() === '';
  });
};

/**
 * The shared rule for a connection that is both switched off and not valid,
 * used by the Platforms grid and the MCP Servers tab alike so one credential
 * gets one label: Finish Setup while no account has been chosen, Deactivated
 * otherwise (the user switched off a credential that has since stopped
 * working). An OAuth link alone proves nothing, since every OAuth platform
 * carries one.
 */
export const getInactiveInvalidStatus = (
  accountChoicePending: boolean
): McpConnectionStatus =>
  accountChoicePending ? 'setup_incomplete' : 'deactivated';

/**
 * Whether a Configure save response rejected the credential. Only an explicit
 * `is_valid: false` is a rejection; a response that does not carry the flag
 * is not proof of anything, so the popup closes and the grid reloads the real
 * state.
 */
export const isMcpSaveRejected = (
  data: { is_valid?: boolean | null } | null | undefined
): boolean => data?.is_valid === false;

/** True when the platform is waiting for the user to (re-)grant consent. */
const needsConsent = (row: McpConnectionRow) =>
  row.oauth_required === true && hasUsableOauthUrl(row);

export const getMcpConnectionStatus = (
  row: McpConnectionRow | null | undefined
): McpConnectionStatus => {
  if (!row) return 'not_connected';

  // No credential row at all. `connection_id` and `is_active` are read from
  // the same map entry in MCP.tsx, so either one missing means "never
  // connected".
  if (!hasValue(row.connection_id) || !hasValue(row.is_active)) {
    return 'not_connected';
  }

  // Built-ins have no credential of their own — never warn on them.
  if (row.is_default === true) {
    return row.is_active === true ? 'connected' : 'deactivated';
  }

  if (row.is_active !== true) {
    // OAuth was completed but the account has not been chosen yet: the
    // controller parks the row inactive AND invalid. That is unfinished
    // setup, not a dead credential. Once an account is saved, an inactive
    // invalid row is one the user switched off.
    if (row.is_valid === false) {
      return getInactiveInvalidStatus(isAccountChoicePending(row));
    }
    return 'deactivated';
  }

  // Switched on, but we cannot tell whether the credential still works.
  // Never green.
  if (!hasValue(row.is_valid)) return 'unknown';

  if (row.is_valid === false) return 'reconnect_needed';

  // Works today, but the grant is gone or about to be: the user has to
  // re-authorise before it breaks.
  if (needsConsent(row)) return 'action_needed';

  return 'connected';
};

/** The pill label and colour for a status, shared by both integration tabs. */
export const getMcpStatusView = (
  status: McpConnectionStatus
): McpConnectionStatusView => ({ status, ...STATUS_VIEW[status] });

export const getMcpConnectionStatusView = (
  row: McpConnectionRow | null | undefined
): McpConnectionStatusView => {
  const status = getMcpConnectionStatus(row);
  return { status, ...STATUS_VIEW[status] };
};

/**
 * The warning tag the Configure popup shows next to the platform's name, as an
 * i18n key — or `null` for the states that deserve no tag at all.
 *
 * It runs through the same decision as the card pill and its tooltip, because
 * `is_valid` on its own cannot tell a credential the backend rejected
 * (`false`) from one whose validity the payload never carried (absent). The
 * popup used to read the flag raw and called both "invalid credentials", so a
 * platform the grid labelled "Status Unknown" was accused of bad credentials
 * the moment it was opened.
 */
export const getMcpCredentialNoticeKey = (
  row: McpConnectionRow | null | undefined
): string | null => {
  switch (getMcpConnectionStatus(row)) {
    case 'reconnect_needed':
      return 'MCP-InvalidCredentials';
    case 'unknown':
      return 'MCP-StatusUnknownHint';
    default:
      return null;
  }
};

/** The "show everything" entry — always first in the filter dropdown. */
export const MCP_STATUS_FILTER_ALL = 'All Status';

/**
 * One filter option per status the helper can return, so no option can select
 * a set that is empty by construction and no status is unreachable from the
 * dropdown. The option strings are the values `MCP` compares against.
 */
export const MCP_STATUS_FILTER_OPTIONS: string[] = [
  MCP_STATUS_FILTER_ALL,
  'Connected',
  'Action Needed',
  'Reconnect Needed',
  'Finish Setup',
  'Deactivated',
  'Not Connected',
  'Status Unknown',
];

const FILTER_TO_STATUS: Record<string, McpConnectionStatus> = {
  Connected: 'connected',
  'Action Needed': 'action_needed',
  'Reconnect Needed': 'reconnect_needed',
  'Finish Setup': 'setup_incomplete',
  Deactivated: 'deactivated',
  'Not Connected': 'not_connected',
  'Status Unknown': 'unknown',
};

/**
 * The translation key for a filter option's displayed label. The options stay
 * English because `MCP` matches on them; only the label is translated, with
 * the same keys as the status pills.
 */
export const getMcpStatusFilterLabelKey = (filter: string): string => {
  const status = FILTER_TO_STATUS[filter];
  if (status) return STATUS_VIEW[status].labelKey;
  return filter === MCP_STATUS_FILTER_ALL ? 'MCP-AllStatuses' : filter;
};

/** The status a filter option selects, or `undefined` for "All Status". */
export const getMcpStatusForFilter = (
  filter: string
): McpConnectionStatus | undefined => FILTER_TO_STATUS[filter];

export const matchesMcpStatusFilter = (
  row: McpConnectionRow | null | undefined,
  filter: string
): boolean => {
  if (filter === MCP_STATUS_FILTER_ALL) return true;
  const wanted = FILTER_TO_STATUS[filter];
  // An unrecognised filter must not blank the grid.
  if (!wanted) return true;
  return getMcpConnectionStatus(row) === wanted;
};

/**
 * Whether a status is a working connection: switched on and accepted by the
 * platform. "Action Needed" still works today, so it counts. A credential the
 * platform refused ("Reconnect Needed"), one whose validity is unknown, an
 * unfinished setup and a switched-off connection do not.
 */
export const isWorkingConnectionStatus = (
  status: McpConnectionStatus
): boolean => status === 'connected' || status === 'action_needed';

/**
 * How many platforms count as connected, for the hub's "Platforms connected"
 * tile. Takes the same rows the grid renders and decides through the same
 * status as the pill, so the tile and the cards can never disagree: a row
 * that is saved but was refused stays visible as Reconnect Needed and is not
 * counted.
 */
export const countConnectedPlatforms = (
  rows: ReadonlyArray<McpConnectionRow | null | undefined>
): number =>
  rows.filter((row) => isWorkingConnectionStatus(getMcpConnectionStatus(row)))
    .length;
